import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-200 motion-safe:ease-out-strong [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground border-border',
        destructive:
          'border-destructive/30 bg-destructive/10 text-destructive-text [&>svg]:text-destructive-text',
        warning:
          'border-warning/30 bg-warning/10 text-warning-text [&>svg]:text-warning-text',
        success:
          'border-success/30 bg-success/10 text-success-text [&>svg]:text-success-text',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

/** Destructive and warning interrupt assistive tech; default and success notices are polite live regions. */
function Alert({ className, variant, role, ref, ...props }: React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants> & { ref?: React.Ref<HTMLDivElement> }) {
  return (
    <div ref={ref} role={role ?? (variant === 'destructive' || variant === 'warning' ? 'alert' : 'status')} className={cn(alertVariants({ variant }), className)} {...props} />
  );
}
Alert.displayName = 'Alert';

function AlertTitle({ className, ref, children, ...props }: React.HTMLAttributes<HTMLHeadingElement> & { ref?: React.Ref<HTMLParagraphElement> }) {
  return (
    <h5 ref={ref} className={cn('mb-1 font-semibold leading-none tracking-tight', className)} {...props}>
      {children}
    </h5>
  );
}
AlertTitle.displayName = 'AlertTitle';

function AlertDescription({ className, ref, ...props }: React.HTMLAttributes<HTMLParagraphElement> & { ref?: React.Ref<HTMLParagraphElement> }) {
  return (
    <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
  );
}
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
